<template>
    <report-tab :name="name" title="Analysis Report" :icon="$icons.reportGears"
                :loading="loading" element-loading-text="Generating report"
                :url-help="$urls.HELP_REPORTS" :click-x="clickX">
        <h3>Table of Contents</h3>
        <ol>
            <!--<li class="toc-bullet">-->
                <!--<el-link type="primary" href="#configurations">Configurations</el-link>-->
            <!--</li>-->
            <li class="toc-bullet" v-for="(dc, i) in dutyCycles" :key="`toc${i}`">
                <el-link type="primary" :href="`#DC${i}`">{{ dc.name }}</el-link>
                <ol v-for="(lc, j) in dc.loadCases" :key="`toc${i}-${j}`">
                    <li class="toc-sub-bullet">
                        <el-link type="primary" :href="`#DC${i}-LC${j}`">{{ lc.name }}</el-link>
                    </li>
                </ol>
            </li>
        </ol>
        <hr>
        <!--PLACEHOLDER {analysis} table of configurations, i.e. clutch state ==> columns: clutches, rows: configs-->
        <!--    ~ See ReportTab, releases table-->
        <!--<div>-->
            <!--<h3 id="configurations">Configurations</h3>-->
            <!--<table"></table>-->
        <!--</div>-->
        <!--<hr>-->
        <div v-for="(dc, i) in dutyCycles" :key="`dc${i}`">
            <div>
                <h3 :id="`DC${i}`">
                    {{ dc.name }}
                    &nbsp;&nbsp;&nbsp;
                    <el-button class="collapse-btn" @click="collapseDutyCycle(dc)" icon="el-icon-minus" type="text"/>
                    <el-button class="collapse-btn" @click="expandDutyCycle(dc)" icon="el-icon-plus" type="text"/>
                </h3>
                <el-collapse v-model="activeLoadCaseSections"
                             v-for="(lc, j) in dc.loadCases" :key="`dc${i}-lc${j}`"
                             @change="changeCollapse"
                             class="collapse-section">
                    <el-collapse-item :id="`DC${i}-LC${j}`" :name="collapseName(lc)">
                        <span slot="title" class="collapse-title">{{ lc.name }}</span>
                        <!--CONDITIONALLY RENDER FOR QUICK INITIAL LOAD-->
                        <div v-if="renderLoadCase[collapseName(lc)]">
                            <!--LC INPUTS-->
                            <report-table-params :table="tableLoadCase(lc)"/>
                            <!--PART RESULTS-->
                            <div v-for="part in parts" :key="`${lc.Id}-${part.Id}`">
                                <h4 class="text">
                                    <dh-icon style-svg="padding-right: .2em; margin-bottom: .2em;" :name="$handlers.model.partIcon(part)"/>
                                    {{ part.label }}
                                </h4>
                                <report-table-params :table="tablePartResults(part, lc)"/>
                            </div>
                            <!--MESH RESULTS-->
                            <div v-for="mesh in meshes" :key="`${lc.Id}-${mesh.Id}`">
                                <h4 class="text">
                                    <dh-icon style-svg="padding-right: .2em; margin-bottom: .2em;" :name="$handlers.model.connIcon(mesh)"/>
                                    {{ mesh.label }}
                                </h4>
                                <report-table-params :table="tableMeshResults(mesh, lc)"/>
                            </div>
                        </div>
                    </el-collapse-item>
                </el-collapse>
            </div>
            <hr>
        </div>
    </report-tab>
</template>

<script>
  import ReportTab from "./ReportTab";
  import ReportTableParams from "./ReportTableParams";
  import math from "../../assets/js/math";
  import {formatValue, sortByString} from "../../assets/js/utils";
  import {convertLinearVelocity, convertTime} from "../../handlers/modules/units";
  import {mapGetters} from "vuex";

  export default {
    name: "ReportAnalysis",
    components: {ReportTab, ReportTableParams},
    props: {
      name: String,  // unique tab name
      clickX: Function,
    },
    units: {
      angularVelocity: 'rpm',
      linearVelocity: 'm/s',
      frequency: 'Hz',
      torque: 'Nm',
      power: 'kW',
      force: 'kN',
      time: 'hr',
    },

    data() {
      return {
        // APOLLO
        parts: [],
        meshes: [],
        configs: [],
        dutyCycles: [],
        loading: true,

        // COLLAPSIBLE SECTIONS
        collapseNames: {},  // {<dcId>: [], ...}
        activeLoadCaseSections: [],
        activeNamesLength: 0,
        renderLoadCase: {},  // conditional rendering
      }
    },
    computed: {
      ...mapGetters({
        getPart: 'getPart',
      }),
    },
    apollo: {
      dutyCycles: {
        manual: true,
        fetchPolicy: 'cache-and-network',
        variables() {return {model: this.$store.state.model.doc.Id}},
        query() {return this.$requests.model.getDutyCyclesFull},  // must include config, powerloads, results
        result({ data }) {
          if (!data) return;

          // map to local state
          this.dutyCycles = data.dutyCycles;
          sortByString(this.dutyCycles, 'name');

          // define collapsible names for each duty cycle
          this.dutyCycles.forEach(dc => {
            this.collapseNames[dc.Id] = dc.loadCases.map(lc => lc.name);
          });

          this.$nextTick(() => this.loading = false);
        },
      },
      // PLACEHOLDER {analysis} query configs for clutch table
      // configs: {},
    },

    mounted() {
      // Called every time the report is opened.

      this.mapParts();
      this.mapMeshes();
    },
    methods: {
      // HANDLERS; probably move to $handlers.model
      tableRow(parameter, value, symbol='', units='-') {
        return {
          parameter: parameter,
          symbol: symbol,
          value: value,
          units: units,
        }
      },
      tableLoadCase(loadCase) {
        // Make a table of load case settings, including any powerload(s).

        // map powerload(s) for tabular display
        const powerloads = loadCase.powerloads.map(powerloadCase => {
          // convert units for powerload params; copy to avoid mutating cache
          powerloadCase = this.$handlers.model.convertLoadCase({...powerloadCase}, undefined, this.$options.units);

          // define table row for speed, torque, and power
          const conn = this.$store.getters.getConnection(powerloadCase.powerload.Id);
          const label = this.$handlers.model.getConnLabel(conn);
          const speed = this.tableRow(`${label}, speed`, formatValue(powerloadCase.speed), '$\\omega_z$', this.$options.units.angularVelocity);
          const torque = this.tableRow(`${label}, torque`, formatValue(powerloadCase.torque), '$\\tau_z$', this.$options.units.torque);
          const power = this.tableRow(`${label}, power`, formatValue(powerloadCase.power), '$P_z$', this.$options.units.power);
          return [speed, torque, power];
        });

        return [
          // PLACEHOLDER {analysis} add href to config row if report has clutch table
          this.tableRow('Configuration', loadCase.config.name),
          this.tableRow('Duration', convertTime(loadCase.duration, undefined, this.$options.units.time), '$t$', this.$options.units.time),
          ...powerloads.flat(),
        ]
      },
      tablePartResults(part, loadCase) {
        // Make a table of part results for the specified load case.
        // loadCase.powerflow.points: [{part, connection, speed, torque, cycles}]
        // PLACEHOLDER {performance} wasteful to filter through all results to find those relevant to a specific part

        // skip if no results exist
        if (!loadCase.powerflow) return [];

        let table = [];
        loadCase.powerflow.points.forEach(powerflowPoint => {
          if (powerflowPoint.part === part.Id) {
            // connection node
            const conn = this.$store.getters.getConnection(powerflowPoint.connection);
            const connectingPart = conn.parts.find(p => p.Id !== part.Id);
            const nodeLabel = `${this.$handlers.model.connTypeLabel(conn)} to ${this.$handlers.model.partLabel(connectingPart)}`;

            // convert units for powerflow results; copy to avoid mutating cache
            powerflowPoint = this.$handlers.model.convertPowerflowPoint({...powerflowPoint, power: powerflowPoint.torque * powerflowPoint.speed}, undefined, this.$options.units);

            // add rows to table
            table.push(this.tableRow('Node', nodeLabel));
            table.push(this.tableRow('Speed', formatValue(powerflowPoint.speed), '$\\omega_z$', this.$options.units.angularVelocity));
            table.push(this.tableRow('Torque', formatValue(powerflowPoint.torque), '$\\tau_z$', this.$options.units.torque));
            table.push(this.tableRow('Power', formatValue(powerflowPoint.power), '$P_z$', this.$options.units.power));
            table.push(this.tableRow('Rotations', math.round(powerflowPoint.cycles)));
            // PLACEHOLDER {analysis} add "load cycles"; gear can have zero angular velocity, e.g. sun
            table.push(this.tableRow('', ''));
          }
        });

        return table;
      },
      tableMeshResults(conn, loadCase) {
        // Make a table of gear mesh results for the specified load case.
        // loadCase.powerflow.meshes: [{connection, cycles, frequency, pitchLineVelocity, forceAxial, forceRadial, forceTangential, forceNormal, forceLoa}]

        // skip if no results exist
        if (!loadCase.powerflow) return [];

        // get results of mesh connection
        let powerflowMesh = loadCase.powerflow.meshes.find(results => results.connection === conn.Id);

        // convert units for powerflow results; copy to avoid mutating cache
        powerflowMesh = this.$handlers.model.convertPowerflowMesh({...powerflowMesh}, undefined, this.$options.units);

        // DEV ONLY
        // console.log('conn', conn);
        // console.log('powerflowMesh', powerflowMesh);

        return [
          this.tableRow('Frequency', formatValue(powerflowMesh.frequency), '$f_m$', this.$options.units.frequency),
          this.tableRow('Pitch line velocity', formatValue(powerflowMesh.pitchLineVelocity), '$v_t$', this.$options.units.linearVelocity),
          this.tableRow('Force, axial', formatValue(powerflowMesh.forceAxial), '$F_z$', this.$options.units.force),
          this.tableRow('Force, radial', formatValue(powerflowMesh.forceRadial), '$F_r$', this.$options.units.force),
          this.tableRow('Force, tangential', formatValue(powerflowMesh.forceTangential), '$F_t$', this.$options.units.force),
          this.tableRow('Force, normal', formatValue(powerflowMesh.forceNormal), '$F_n$', this.$options.units.force),
          this.tableRow('Force, line of action', formatValue(powerflowMesh.forceLoa), '$F_\\text{loa}$', this.$options.units.force),
          this.tableRow('Cycles', math.round(powerflowMesh.cycles)),
        ];
      },

      // APOLLO
      mapParts() {
        // Get parts from cache and map to local state.

        // get all parts; should exclude ground
        this.parts = this.$store.getters.getParts(this.$requests.model.partQueryStr).map(part => {
          return {
            ...part,
            label: this.$handlers.model.partLabel(part),
          }
        });

        // order the same as feature tree
        sortByString(this.parts, 'label');
      },
      mapMeshes() {
        // Get ratio connections from cache and map to local state.

        // get ratio connections
        const fragment = this.$requests.model.fragmentPartLabel;
        this.meshes = this.$store.getters.getConnections(this.$requests.model.ConnectionType.RATIO, fragment).map(conn => {
          return {
            ...conn,
            label: this.$handlers.model.getConnLabel(conn),
          }
        });

        // order the same as feature tree
        sortByString(this.meshes, 'label');
      },

      // COLLAPSE NAMES
      collapseName(item) {
        return item.Id;
      },
      collapseDutyCycleNames(dutyCycle) {
        // Get collapsible name of each load case in a duty cycle.
        return dutyCycle.loadCases.map(loadCase => this.collapseName(loadCase));
      },

      // COLLAPSE EVENTS
      changeCollapse(activeNames) {
        // As defined by this.collapseName(), activeNames is an array of load case Id.

        const activeNamesLength = activeNames.length;
        if (activeNamesLength > this.activeNamesLength) {
          // expanded collapsible
          const expandedName = activeNames[activeNamesLength-1];

          // check if collapsible has been loaded previously
          if (!this.renderLoadCase[expandedName]) {
            // for-loop used to handle use case of multiple expansions
            activeNames.forEach(name => this.renderLoadCase[name] = true);

            this.$nextTick(() => {
              window.MathJax.typeset();
            });
          }
        }

        // update active name tracker
        this.activeNamesLength = activeNamesLength;
      },
      collapseDutyCycle(dutyCycle) {
        // Collapse the collapsible for each load case in a duty cycle.
        const removeNames = this.collapseDutyCycleNames(dutyCycle);
        this.activeLoadCaseSections = this.activeLoadCaseSections.filter(name => !removeNames.includes(name));
        this.changeCollapse(this.activeLoadCaseSections);
      },
      expandDutyCycle(dutyCycle) {
        // Expand the collapsible for each load case in a duty cycle.
        this.activeLoadCaseSections.push(...this.collapseDutyCycleNames(dutyCycle));
        this.changeCollapse(this.activeLoadCaseSections);
      },
    },
  }
</script>

<style scoped lang="scss" src="./assets/report-content.scss"/>
